import { z } from 'zod';
export const CustomerAnalyticsQuerySchema = z.object({
    customer_id: z.string().optional(),
    date_range: z.object({
        start: z.string().date(),
        end: z.string().date(),
    }),
    granularity: z.enum(['day', 'week', 'month']).default('day').optional(),
    metrics: z
        .array(z.enum(['usage', 'revenue', 'engagement', 'roi', 'lifetime_value']))
        .default(['usage', 'revenue'])
        .optional(),
    segment_by: z
        .enum(['agent', 'signal', 'contract', 'organization'])
        .optional(),
    top_n: z.number().min(1).max(100).default(10).optional(),
});
export const CustomerAnalyticsResponseSchema = z.object({
    date_range: z.object({ start: z.string().date(), end: z.string().date() }),
    summary: z.object({
        total_customers: z.number(),
        active_customers: z.number(),
        new_customers: z.number(),
        churned_customers: z.number(),
        total_revenue: z.number(),
        average_revenue_per_customer: z.number(),
        customer_lifetime_value: z.number(),
        churn_rate: z.number(),
    }),
    time_series: z.array(z.object({
        date: z.string().date(),
        active_customers: z.number(),
        new_customers: z.number(),
        revenue: z.number(),
        usage: z.number(),
    })),
    top_customers: z.array(z.object({
        customer_id: z.string(),
        customer_name: z.string(),
        revenue: z.number(),
        usage: z.number(),
        growth_rate: z.number(),
        risk_score: z.number(),
    })),
    cohort_analysis: z
        .object({
        periods: z.array(z.string()),
        cohorts: z.array(z.object({
            cohort_month: z.string(),
            customer_count: z.number(),
            retention_rates: z.array(z.number()),
        })),
    })
        .optional(),
    segmentation: z
        .array(z.object({
        segment_key: z.string(),
        segment_name: z.string(),
        customer_count: z.number(),
        revenue: z.number(),
        usage: z.number(),
        percentage: z.number(),
    }))
        .optional(),
});
export const ROIAnalyticsQuerySchema = z.object({
    date_range: z.object({
        start: z.string().date(),
        end: z.string().date(),
    }),
    agent_id: z.string().optional(),
    signal_id: z.string().optional(),
    customer_segment: z.string().optional(),
    include_projections: z.boolean().default(false).optional(),
});
export const ROIAnalyticsResponseSchema = z.object({
    summary: z.object({
        total_investment: z.number(),
        total_revenue: z.number(),
        net_profit: z.number(),
        roi_percentage: z.number(),
        payback_period_days: z.number().nullable(),
        break_even_date: z.string().date().nullable(),
    }),
    agent_roi: z.array(z.object({
        agent_id: z.string(),
        agent_name: z.string(),
        investment: z.number(),
        revenue: z.number(),
        profit: z.number(),
        roi_percentage: z.number(),
        efficiency_score: z.number(),
    })),
    signal_roi: z.array(z.object({
        signal_id: z.string(),
        signal_name: z.string(),
        agent_name: z.string(),
        cost_per_unit: z.number(),
        revenue_per_unit: z.number(),
        margin_per_unit: z.number(),
        volume: z.number(),
        total_profit: z.number(),
    })),
    trends: z.array(z.object({
        date: z.string().date(),
        investment: z.number(),
        revenue: z.number(),
        roi_percentage: z.number(),
    })),
    projections: z
        .object({
        next_30_days: z.object({
            projected_revenue: z.number(),
            projected_profit: z.number(),
            confidence_interval: z.object({ lower: z.number(), upper: z.number() }),
        }),
        next_90_days: z.object({
            projected_revenue: z.number(),
            projected_profit: z.number(),
            confidence_interval: z.object({ lower: z.number(), upper: z.number() }),
        }),
    })
        .optional(),
});
export const CustomAnalyticsQuerySchema = z.object({
    query_name: z.string().optional(),
    data_sources: z.array(z.enum([
        'events',
        'customers',
        'agents',
        'contracts',
        'invoices',
        'payments',
    ])),
    date_range: z.object({
        start: z.string().datetime(),
        end: z.string().datetime(),
    }),
    filters: z.record(z.string(), z.unknown()),
    dimensions: z.array(z.string()),
    metrics: z.array(z.object({
        field: z.string(),
        aggregation: z.enum([
            'sum',
            'count',
            'avg',
            'min',
            'max',
            'distinct_count',
            'percentile_95',
            'percentile_99',
        ]),
        alias: z.string().optional(),
    })),
    time_bucket: z.enum(['hour', 'day', 'week', 'month']).optional(),
    limit: z.number().min(1).max(10000).default(1000).optional(),
    format: z.enum(['json', 'csv', 'parquet']).default('json').optional(),
});
export const CustomAnalyticsResponseSchema = z.object({
    query_name: z.string().nullable(),
    data: z.array(z.record(z.string(), z.unknown())),
    metadata: z.object({
        total_rows: z.number(),
        execution_time_ms: z.number(),
        query_hash: z.string(),
        cached: z.boolean(),
    }),
});
export const AgentAnalyticsQuerySchema = z.object({
    date_range: z.object({
        start: z.string().date(),
        end: z.string().date(),
    }),
    granularity: z.enum(['day', 'week', 'month']).default('day').optional(),
    metrics: z
        .array(z.enum(['usage', 'revenue', 'cost', 'profit']))
        .default(['usage', 'revenue'])
        .optional(),
});
export const AgentAnalyticsResponseSchema = z.object({
    agent_id: z.string(),
    agent_name: z.string(),
    date_range: z.object({ start: z.string().date(), end: z.string().date() }),
    summary: z.object({
        total_usage: z.number(),
        total_revenue: z.number(),
        total_cost: z.number(),
        profit_margin: z.number(),
        unique_customers: z.number(),
        avg_usage_per_customer: z.number(),
    }),
    time_series: z.array(z.object({
        date: z.string().date(),
        usage: z.number(),
        revenue: z.number(),
        cost: z.number(),
        customers: z.number(),
    })),
    top_signals: z.array(z.object({
        signal_id: z.string(),
        signal_name: z.string(),
        usage: z.number(),
        revenue: z.number(),
        percentage: z.number(),
    })),
});
export const RevenueAnalyticsQuerySchema = z.object({
    date_range: z.object({
        start: z.string().date(),
        end: z.string().date(),
    }),
    granularity: z.enum(['day', 'week', 'month']).default('day').optional(),
    segment_by: z.enum(['agent', 'customer', 'contract', 'signal']).optional(),
    currency: z.string().default('USD').optional(),
});
export const RevenueAnalyticsResponseSchema = z.object({
    date_range: z.object({ start: z.string().date(), end: z.string().date() }),
    summary: z.object({
        total_revenue: z.number(),
        recurring_revenue: z.number(),
        one_time_revenue: z.number(),
        growth_rate: z.number(),
        average_revenue_per_customer: z.number(),
    }),
    time_series: z.array(z.object({
        date: z.string().date(),
        revenue: z.number(),
        recurring: z.number(),
        one_time: z.number(),
    })),
    segments: z
        .array(z.object({
        segment_key: z.string(),
        segment_name: z.string(),
        revenue: z.number(),
        percentage: z.number(),
        growth_rate: z.number(),
    }))
        .optional(),
});
export const CostAnalyticsQuerySchema = z.object({
    date_range: z.object({
        start: z.string().date(),
        end: z.string().date(),
    }),
    granularity: z.enum(['day', 'week', 'month']).default('day').optional(),
    cost_type: z
        .enum(['infrastructure', 'processing', 'storage', 'all'])
        .default('all')
        .optional(),
    agent_id: z.string().optional(),
});
export const CostAnalyticsResponseSchema = z.object({
    date_range: z.object({ start: z.string().date(), end: z.string().date() }),
    summary: z.object({
        total_cost: z.number(),
        infrastructure_cost: z.number(),
        processing_cost: z.number(),
        storage_cost: z.number(),
        cost_per_transaction: z.number(),
    }),
    time_series: z.array(z.object({
        date: z.string().date(),
        total_cost: z.number(),
        infrastructure: z.number(),
        processing: z.number(),
        storage: z.number(),
    })),
    cost_breakdown: z.array(z.object({
        category: z.string(),
        amount: z.number(),
        percentage: z.number(),
    })),
});
export const CustomerValueAnalyticsQuerySchema = z.object({
    date_range: z.object({
        start: z.string().date(),
        end: z.string().date(),
    }),
    customer_segment: z.string().optional(),
    value_metric: z
        .enum(['lifetime_value', 'monthly_value', 'usage_value', 'all'])
        .default('all')
        .optional(),
});
export const CustomerValueAnalyticsResponseSchema = z.object({
    date_range: z.object({ start: z.string().date(), end: z.string().date() }),
    summary: z.object({
        average_lifetime_value: z.number(),
        average_monthly_value: z.number(),
        total_customer_value: z.number(),
        value_growth_rate: z.number(),
    }),
    customer_segments: z.array(z.object({
        segment: z.string(),
        customer_count: z.number(),
        average_value: z.number(),
        total_value: z.number(),
        percentage: z.number(),
    })),
    value_distribution: z.array(z.object({
        range: z.string(),
        customer_count: z.number(),
        percentage: z.number(),
    })),
});
export const AnalyticsDashboardResponseSchema = z.object({
    dashboard: z.object({
        summary: z.object({
            total_customers: z.number(),
            total_revenue: z.number(),
            total_usage: z.number(),
            active_contracts: z.number(),
        }),
        trends: z.object({
            customers: z.array(z.object({ date: z.string(), value: z.number() })),
            revenue: z.array(z.object({ date: z.string(), value: z.number() })),
            usage: z.array(z.object({ date: z.string(), value: z.number() })),
        }),
        top_customers: z.array(z.object({
            id: z.string(),
            name: z.string(),
            revenue: z.number(),
            usage: z.number(),
        })),
        top_agents: z.array(z.object({
            id: z.string(),
            name: z.string(),
            usage: z.number(),
            cost: z.number(),
        })),
    }),
});
export const AnalyticsUsageResponseSchema = z.object({
    analytics: z.object({
        total_usage: z.number(),
        period_usage: z.array(z.object({
            date: z.string(),
            usage: z.number(),
            cost: z.number(),
            unique_customers: z.number(),
        })),
        breakdown_by_signal: z.array(z.object({
            signal_id: z.string(),
            signal_name: z.string(),
            usage: z.number(),
            percentage: z.number(),
        })),
        breakdown_by_customer: z.array(z.object({
            customer_id: z.string(),
            customer_name: z.string(),
            usage: z.number(),
            percentage: z.number(),
        })),
    }),
});
export const AnalyticsRevenueResponseSchema = z.object({
    revenue: z.object({
        total_revenue: z.number(),
        recurring_revenue: z.number(),
        one_time_revenue: z.number(),
        revenue_growth_rate: z.number(),
        monthly_revenue: z.array(z.object({
            month: z.string(),
            revenue: z.number(),
            growth_rate: z.number(),
        })),
        revenue_by_customer_type: z.array(z.object({
            type: z.string(),
            revenue: z.number(),
            percentage: z.number(),
        })),
        revenue_by_feature: z.array(z.object({
            feature_id: z.string(),
            feature_name: z.string(),
            revenue: z.number(),
            percentage: z.number(),
        })),
    }),
});
export const AnalyticsCustomersResponseSchema = z.object({
    customers: z.object({
        total_customers: z.number(),
        new_customers_this_month: z.number(),
        churned_customers_this_month: z.number(),
        customer_lifetime_value: z.number(),
        segments: z.array(z.object({
            segment: z.string(),
            count: z.number(),
            percentage: z.number(),
            avg_revenue: z.number(),
        })),
        retention_rates: z.array(z.object({ period: z.string(), rate: z.number() })),
        top_customers: z.array(z.object({
            customer_id: z.string(),
            customer_name: z.string(),
            revenue: z.number(),
            usage: z.number(),
            growth_rate: z.number(),
        })),
    }),
});
export const AnalyticsAgentsResponseSchema = z.object({
    agents: z.object({
        total_agents: z.number(),
        active_agents: z.number(),
        total_usage: z.number(),
        total_cost: z.number(),
        efficiency_metrics: z.object({
            avg_response_time: z.number(),
            success_rate: z.number(),
            cost_per_request: z.number(),
        }),
        agent_performance: z.array(z.object({
            agent_id: z.string(),
            agent_name: z.string(),
            usage: z.number(),
            cost: z.number(),
            efficiency_score: z.number(),
            success_rate: z.number(),
            avg_response_time: z.number(),
        })),
        usage_trends: z.array(z.object({
            date: z.string(),
            total_usage: z.number(),
            cost: z.number(),
        })),
    }),
});
export const MonetizationSummaryQuerySchema = z.object({
    from_date: z.string(),
    to_date: z.string(),
});
export const MonetizationAgentSummarySchema = z.object({
    agent_id: z.string(),
    agent_name: z.string(),
    status: z.string(),
    task_runs: z.number(),
    workflow_runs: z.number(),
    outcome_runs: z.number(),
    revenue_usd: z.number(),
    cost_usd: z.number(),
    margin_usd: z.number(),
    margin_percentage: z.number(),
});
export const MonetizationTimeSeriesSchema = z.object({
    date: z.string(),
    revenue_usd: z.number(),
    cost_usd: z.number(),
    margin_usd: z.number(),
    task_runs: z.number(),
    workflow_runs: z.number(),
    outcome_runs: z.number(),
});
export const MonetizationSummaryResponseSchema = z.object({
    summary: z.object({
        total_agents: z.number(),
        total_task_runs: z.number(),
        total_workflow_runs: z.number(),
        total_outcome_runs: z.number(),
        total_revenue_usd: z.number(),
        total_cost_usd: z.number(),
        total_margin_usd: z.number(),
    }),
    agents: z.array(MonetizationAgentSummarySchema),
    time_series: z.array(MonetizationTimeSeriesSchema),
});
//# sourceMappingURL=index.js.map